/**
 * The theme toggle's state (ADR-027, amended by Mahmoud's ruling 2026-10-02).
 *
 * THE ORDER OF PRECEDENCE
 *   1. An explicit choice, persisted in localStorage, wins forever.
 *   2. DARK is the default: a first-visit user sees dark, whatever the operating
 *      system asks for. The OS preference is deliberately not consulted.
 *
 * The choice is applied by writing `data-theme` onto the document element, which is the
 * hook the token stylesheet styles the alternate set from.
 */

export type Theme = "light" | "dark";

const THEME_KEY = "qualify.theme";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

/** The persisted explicit choice, or null when this visitor has never made one. */
export function storedTheme(storage: Pick<Storage, "getItem"> = localStorage): Theme | null {
  const raw = storage.getItem(THEME_KEY);
  return isTheme(raw) ? raw : null;
}

/** The theme a fresh page load shows: the explicit choice, else dark. */
export function initialTheme(storage: Pick<Storage, "getItem"> = localStorage): Theme {
  return storedTheme(storage) ?? "dark";
}

/** Write the choice onto the document element so the token stylesheet picks it up. */
export function applyTheme(theme: Theme, element: HTMLElement = document.documentElement): void {
  element.dataset.theme = theme;
}

/** An explicit choice: persist it, then apply it. */
export function setTheme(
  theme: Theme,
  storage: Pick<Storage, "getItem" | "setItem"> = localStorage,
  element: HTMLElement = document.documentElement,
): void {
  storage.setItem(THEME_KEY, theme);
  applyTheme(theme, element);
}
